import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MapPin, Search, Store, Warehouse, X } from "lucide-react";

import type { PlaceType } from "@/types/api";
import { noAutofill } from "@/lib/noAutofill";

export interface PickableSpot {
  id: number;
  name: string;
  address: string;
  place_type?: PlaceType;
  tables_count?: number | null;
}

interface Props {
  spots: PickableSpot[];
  value: number | null;
  onChange: (id: number | null) => void;
  /** Hide specific spots from the list (e.g. already used). */
  excludeIds?: number[];
  placeholder?: string;
  /** Caption shown as the "no selection" empty option. Null disables it. */
  emptyLabel?: string | null;
  /** Placeholder shown on the closed field when nothing is selected. */
  closedPlaceholder?: string;
}

const PLACE_LABEL: Record<PlaceType, string> = {
  outdoor_spot: "Уличный",
  indoor_hall: "Зал",
  store: "Магазин",
};

function IconForType({ type, size = 16 }: { type?: PlaceType; size?: number }) {
  const common = { size, color: "var(--fg3)" } as const;
  if (type === "indoor_hall") return <Warehouse {...common} />;
  if (type === "store") return <Store {...common} />;
  return <MapPin {...common} />;
}

function matches(s: PickableSpot, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    s.name.toLowerCase().includes(needle) ||
    s.address.toLowerCase().includes(needle)
  );
}

/**
 * Click-to-expand spot picker. Closed state shows a single field with the
 * selected spot (or a placeholder); clicking opens a dropdown with search
 * input and a filtered list. Selecting auto-closes.
 */
export function SpotPicker({
  spots,
  value,
  onChange,
  excludeIds,
  placeholder = "Поиск по названию или адресу",
  emptyLabel,
  closedPlaceholder = "— выбери площадку —",
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const excludeSet = useMemo(
    () => new Set(excludeIds ?? []),
    [excludeIds],
  );

  const filtered = useMemo(
    () =>
      spots.filter((s) => !excludeSet.has(s.id) && matches(s, query.trim())),
    [spots, excludeSet, query],
  );

  const selected = value != null ? spots.find((s) => s.id === value) : null;

  // Auto-focus the search input when the dropdown opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (id: number | null) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          textAlign: "left",
          background: "var(--bg-section)",
          border: "1px solid var(--separator)",
          borderRadius: "var(--r-md)",
          padding: "10px 12px",
          color: "var(--fg1)",
          cursor: "pointer",
          fontFamily: "var(--font-body)",
        }}
      >
        <span
          style={{
            width: 32,
            height: 32,
            flex: "none",
            borderRadius: 999,
            background: selected
              ? "var(--bg-brand-ghost, rgba(255,90,31,0.12))"
              : "var(--bg-secondary)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: selected ? "var(--brand-orange)" : "var(--fg3)",
          }}
        >
          <IconForType type={selected?.place_type} />
        </span>

        <span style={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <>
              <span
                className="body"
                style={{
                  display: "block",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selected.name}
              </span>
              <span
                className="caption"
                style={{
                  display: "block",
                  color: "var(--fg3)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selected.place_type ? `${PLACE_LABEL[selected.place_type]} · ` : ""}
                {selected.address}
              </span>
            </>
          ) : (
            <span className="body" style={{ color: "var(--fg3)" }}>
              {closedPlaceholder}
            </span>
          )}
        </span>

        {selected ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              pick(null);
            }}
            aria-label="Сбросить"
            style={{
              background: "transparent",
              border: 0,
              padding: 6,
              color: "var(--fg3)",
              cursor: "pointer",
              display: "inline-flex",
            }}
          >
            <X size={16} />
          </button>
        ) : (
          <ChevronDown
            size={18}
            color="var(--fg3)"
            style={{
              transition: "transform var(--d-fast, 150ms) ease",
              transform: open ? "rotate(180deg)" : undefined,
            }}
          />
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: "var(--bg-section)",
            border: "1px solid var(--separator)",
            borderRadius: "var(--r-md)",
            boxShadow: "var(--sh-2, 0 6px 20px rgba(16,19,26,0.08))",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              borderBottom: "1px solid var(--separator)",
            }}
          >
            <Search size={16} color="var(--fg3)" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              {...noAutofill}
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: 0,
                background: "transparent",
                color: "var(--fg1)",
                fontSize: 15,
                fontFamily: "var(--font-body)",
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Очистить"
                style={{
                  background: "transparent",
                  border: 0,
                  padding: 2,
                  color: "var(--fg3)",
                  cursor: "pointer",
                  display: "inline-flex",
                }}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div
            style={{
              maxHeight: 360,
              overflowY: "auto",
            }}
          >
            {emptyLabel != null && !query && (
              <button
                type="button"
                onClick={() => pick(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  background: "transparent",
                  border: 0,
                  padding: "10px 12px",
                  cursor: "pointer",
                  textAlign: "left",
                  color: "var(--fg2)",
                  fontStyle: "italic",
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    background: "var(--bg-secondary)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--fg3)",
                    flex: "none",
                  }}
                >
                  <X size={14} />
                </span>
                <span className="body">{emptyLabel}</span>
              </button>
            )}

            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "16px 14px",
                  color: "var(--fg2)",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                Ничего не нашли
              </div>
            ) : (
              filtered.map((s, i) => {
                const isFirst = i === 0 && !(emptyLabel != null && !query);
                const isSelected = s.id === value;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => pick(s.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      background: isSelected
                        ? "var(--bg-brand-ghost, rgba(255,90,31,0.08))"
                        : "transparent",
                      border: 0,
                      borderTop: isFirst ? 0 : "1px solid var(--separator)",
                      padding: "10px 12px",
                      cursor: "pointer",
                      textAlign: "left",
                      color: "var(--fg1)",
                    }}
                  >
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 999,
                        background: "var(--bg-secondary)",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "none",
                      }}
                    >
                      <IconForType type={s.place_type} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        className="body"
                        style={{
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.name}
                      </div>
                      <div
                        className="caption"
                        style={{
                          color: "var(--fg3)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.place_type ? `${PLACE_LABEL[s.place_type]} · ` : ""}
                        {s.address}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
